
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Users, Building, Globe, Mail, Phone, Briefcase } from 'lucide-react';
import type { Contact } from '@/pages/PersonalizeOutreach';

interface ContactReviewProps {
  contacts: Contact[];
  onContactsConfirmed: (contacts: Contact[]) => void;
}

const ContactReview = ({ contacts, onContactsConfirmed }: ContactReviewProps) => {
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>(contacts);

  const removeContact = (contactId: string) => {
    setSelectedContacts(prev => prev.filter(contact => contact.id !== contactId));
  };

  const handleConfirm = () => {
    onContactsConfirmed(selectedContacts);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Review Contacts
            </div>
            <Badge variant="secondary">
              {selectedContacts.length} of {contacts.length} contacts
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Review the uploaded contacts and remove any you don't want to include in the outreach campaign.
          </p>
          
          <div className="grid gap-4">
            {selectedContacts.map((contact) => (
              <div
                key={contact.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">{contact.person || 'Unknown Person'}</span>
                      </div>
                      {contact.organization && (
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-gray-500" />
                          <span>{contact.organization}</span>
                        </div>
                      )}
                      {contact.title && (
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">{contact.title}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 flex-wrap text-sm">
                      {contact.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-gray-500" />
                          <a 
                            href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {contact.website}
                          </a>
                        </div>
                      )}
                      {contact.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">{contact.email}</span>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-600">{contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeContact(contact.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {selectedContacts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No contacts selected. All contacts have been removed.</p>
            </div>
          )}
          
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <p className="text-sm text-gray-600">
              {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} ready for research
            </p>
            <Button 
              onClick={handleConfirm}
              disabled={selectedContacts.length === 0}
              size="lg"
            >
              Continue with Selected Contacts
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactReview;
